import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react'
import { filterFns } from '@tanstack/react-table'
import sortingUtils from '../../../utils/sorting.utils'
import CapsuleConfigurationTable from './Tables/CapsuleConfigurationTable'
import { getColumsVisibility } from '../../../utils/table.utils'
import AddMixtureTable from './Tables/AddMixtureTable'
import { useCapsuleConfigurator } from '../../../context/CapsuleConfiguratorContext'
import CapsuleSelectCell from './Tables/CapsuleSelectCell'
import { createNameOptionsFromIndex, formatDefaultConfigurationToAddCapsule, getMixtureCountByCapsule } from '../../../utils/capsule_configuration.utils'
import api from '../../../middleware/api'
import { divideConfigurationByCapsule, formatCapsuleConfigurationForDatabase } from '../../../utils/format.utils'
import { toast } from 'react-toastify'
import { useNavigate, useOutletContext } from 'react-router-dom'
import SensiblityCell from './Tables/SensiblityCell'
import DilutionCell from './Tables/DilutionCell'
import PreTestSelector from './Tables/PreTestSelector'
import { useAuth } from '../../../context/AuthContext'
import ActionCell from './Tables/ActionCell'
import TableCheckbox from './Tables/TableCheckbox'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { ArrowLeft, ArrowLeftIcon, Plus } from 'lucide-react'
import ReagentsModal from '../../Capsules/ReagentsModal'
import SaveConfigurationButton from './SaveConfigurationButton'

// Extraire les boutons en composants mémoïsés
const ActionButton = React.memo(({ onClick, disabled, className, children }) => (
  <button
    onClick={onClick}
    className={`btn btn-sm ${className}`}
    disabled={disabled}
  >
    {children}
  </button>
));

// Créer un composant mémoïsé pour les badges de capsule
const CapsuleBadge = React.memo(({ name, count, onDelete }) => (
  <div className="badge badge-outline badge-lg badge-primary flex-shrink-0 space-x-2 min-w-[100px] h-[30px]" data-tip={``}>
    <span>{name} :</span>
    <span>{count}</span>
    <XMarkIcon onClick={() => onDelete(name)} className='w-4 h-4' />
  </div>
));

// Composant mémoïsé pour le dropdown
const DivisionDropdown = React.memo(({ isModified, isSaving, duplicateEffluences, duplicateEffluencesInput, handleDuplicateEffluences, handleCreateMultipleCapsule }) => (
  <div className="dropdown dropdown-end">
    <div
      tabIndex={0}
      role="button"
      className={`btn btn-sm text-base border-solid ${!isSaving ? 'btn-primary' : 'btn-disabled'} rounded-btn`}
    >
      {isSaving ? 'Enregistrement...' : 'Diviser'}
    </div>
    <ul tabIndex={0} className="dropdown-content menu bg-base-100 w-max rounded-box z-[1] p-2 shadow flex flex-col gap-2">
      <li>
        <label className="cursor-pointer p-2 label gap-2">
          <span className="label-text">Dupliquer les éffluences</span>
          <input
            ref={duplicateEffluencesInput}
            type="checkbox"
            checked={duplicateEffluences}
            onChange={handleDuplicateEffluences}
            className="checkbox checkbox-xs checkbox-primary border-solid"
            disabled={isSaving}
          />
        </label>
      </li>
      <li
        onClick={!isSaving ? handleCreateMultipleCapsule : null}
        className={`btn btn-sm text-white border-solid ${!isSaving ? 'btn-primary' : 'btn-disabled'} rounded-btn`}
      >
        Confirmer
      </li>
    </ul>
  </div>
));

const CapsuleConfiguration = ({ }) => {

  const { setCapsuleConfiguration, capsuleConfiguration, showList, setCapsuleConfiguration: setConfigurations, setShowList, defaultCapsule, contentConfiguration: content, selectedConfigurationId, setCountByCapsule, countByCapsule, synchronizeConfigurations } = useCapsuleConfigurator()
  const { setData: updateCustomerData, customer, capsules } = useOutletContext()
  const { user } = useAuth()
  const defaultDropdownOptions = [{ id: 0, name: defaultCapsule ? defaultCapsule.name : 'Capsule A', databaseId: defaultCapsule ? defaultCapsule.id : null }]
  const [capsulesDropdownOptions, setCapsulesDropdownOptions] = useState(defaultDropdownOptions)
  // Ajouter une vérification sécurisée pour content
  const safeContent = content || [];
  const [duplicateEffluences, setDuplicateEffluences] = useState(false)
  const [isDivisionMode, setIsDivisionMode] = useState(false); // Nouvel état pour le mode division
  const [isModified, setIsModified] = useState(false); // Nouvel état pour suivre les modifications
  const [initialConfiguration, setInitialConfiguration] = useState(null); // Pour stocker l'état initial
  const [isSaving, setIsSaving] = useState(false); // Ajout d'un état pour le chargement
  const duplicateEffluencesInput = useRef(null)
  const naviguate = useNavigate()
  const isNewCapsule = defaultCapsule && defaultCapsule.configurations && defaultCapsule.configurations[0] &&
    defaultCapsule.configurations[0].configuration &&
    defaultCapsule.configurations[0].configuration.content &&
    defaultCapsule.configurations[0].configuration.content.length === 0





  // Détecter si on est en train de créer une nouvelle configuration
  // Si la capsule a plusieurs configurations et que selectedConfigurationId ne correspond à aucune configuration existante
  const isNewConfiguration = useMemo(() => {
    if (!defaultCapsule || !defaultCapsule.configurations) return false;
    if (defaultCapsule.configurations.length <= 1) return false;

    // Vérifier si selectedConfigurationId correspond à une configuration existante
    const existingConfig = defaultCapsule.configurations.find(config => config.id === selectedConfigurationId);

    // Si la configuration est nouvelle (n'existe pas encore dans la liste)
    return !existingConfig;
  }, [defaultCapsule, selectedConfigurationId]);

  // Déterminer si la configuration actuelle est la configuration initiale
  // La configuration initiale est celle avec l'ID le plus petit
  const isInitialConfiguration = useMemo(() => {
    if (!defaultCapsule || !defaultCapsule.configurations || defaultCapsule.configurations.length === 0) return true;

    // Trouver la configuration avec l'ID le plus petit
    const initialConfig = defaultCapsule.configurations.reduce((initial, current) => {
      return (current.id < initial.id) ? current : initial;
    }, defaultCapsule.configurations[0]);

    // Si nous créons une nouvelle configuration et qu'aucune config n'existe, c'est l'initiale
    if (isNewConfiguration && defaultCapsule.configurations.length === 0) return true;

    // Si nous créons une nouvelle configuration et des configs existent déjà, ce n'est pas l'initiale
    if (isNewConfiguration && defaultCapsule.configurations.length > 0) return false;

    // Si nous modifions une configuration existante, vérifier si c'est l'initiale
    return initialConfig.id === selectedConfigurationId;
  }, [defaultCapsule, selectedConfigurationId, isNewConfiguration]);

  // Fonction pour obtenir la configuration initiale de la capsule
  const getInitialConfiguration = useMemo(() => {
    if (!defaultCapsule || !defaultCapsule.configurations || defaultCapsule.configurations.length === 0) return null;

    // Trouver la configuration avec l'ID le plus petit (configuration initiale)
    const initialConfig = defaultCapsule.configurations.reduce((initial, current) => {
      return (current.id < initial.id) ? current : initial;
    }, defaultCapsule.configurations[0]);

    return initialConfig;
  }, [defaultCapsule]);

  const configurationDef = [
    {
      id: 'select',
      header: ({ table }) => (
        <TableCheckbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onChange: table.getToggleAllRowsSelectedHandler(),
          }}
        />
      ),
      cell: ({ row }) => {

        return (
          <div className="px-1">
            <TableCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        )
      },
      footer: () => null,
      enableResizing: false,
      enableGlobalFilter: false,
      size: 20,
    },
    {
      id: 'capsule.name',
      accessorFn: row => row?.capsule?.name,
      header: () => <span>Capsule</span>,
      cell: info => <CapsuleSelectCell options={capsulesDropdownOptions} value={info?.row?.original?.capsule?.id} row={info?.row} table={info?.table} />,
      footer: props => props.column.id,
      filterFn: filterFns.equalsString,
      sortingFn: sortingUtils.sortText,
      enableHiding: true,
      hideByDefault: true,
      size: 50

    },
    {
      id: 'mixture.name',
      accessorFn: row => row?.mixture?.name,
      header: () => <span>Nom</span>,
      cell: info => <span className={`${showList ? 'truncate' : ''} ${info.row.original.mixture.category === 'Effluences' ? 'text-accent' : ''}`} >{info.getValue()}</span>,
      footer: props => props.column.id,
      filterFn: filterFns.includesString,
      sortingFn: sortingUtils.sortText,
      size: 150

    },
    // Colonne "Sensibilité Initiales" qui n'apparaît qu'en mode validation
    ...(!isInitialConfiguration ? [{
      id: 'initialSensibility',
      accessorFn: row => {
        if (!getInitialConfiguration || !getInitialConfiguration.configuration || !getInitialConfiguration.configuration.content) return '-';

        // Trouver la sensibilité correspondante dans la configuration initiale
        const initialRow = getInitialConfiguration.configuration.content.find(
          initRow => initRow.mixture.id === row.mixture.id
        );

        return initialRow && initialRow.sensibility !== null ? initialRow.sensibility : '-';
      },
      header: () => <span className='truncate'>Sensibilité</span>,
      cell: info => {
        const value = info.getValue();
        const className = value !== '-' ? 'badge badge-sm badge-primary' : '';
        return <span className={className}>{value}</span>;
      },
      footer: props => props.column.id,
      sortingFn: sortingUtils.sortNumber,
      filterFn: filterFns.inNumberRange,
      enableHiding: true,
      size: 125
    }] : []),
    {
      id: 'sensibility',
      accessorFn: row => row?.sensibility || '-',
      header: () => <span>{isInitialConfiguration ? 'Sensibilité' : 'Validation'}</span>,
      cell: info => <SensiblityCell value={info?.row?.original?.sensibility} row={info?.row} table={info?.table} />,
      footer: props => props.column.id,
      sortingFn: sortingUtils.sortNumber,
      filterFn: filterFns.inNumberRange,
      enableHiding: true,
      size: 200
    },
    {
      id: 'dilution.label',
      accessorFn: row => row?.dilution?.label,
      header: () => <span>Dilution</span>,
      cell: info => {
        const hasDilution = info?.row.original?.dilution || null
        const defaultDilution = info?.row.original?.mixture?.defaultDilution
        return <DilutionCell value={hasDilution ? hasDilution.id : defaultDilution.id} row={info?.row} table={info?.table} />
      },
      footer: props => props.column.id,
      sortingFn: sortingUtils.sortText,
      filterFn: filterFns.includesString,
      enableHiding: true,
      size: 50
    },
    {
      id: 'mixture.subCategory',
      accessorFn: row => row?.mixture?.subCategory,
      header: () => <span>Catégories</span>,
      cell: info => <span >{info.getValue()}</span>,
      footer: props => props.column.id,
      sortingFn: sortingUtils.sortSubCategory,
      filterFn: filterFns.includesString,
      enableHiding: true,
      size: 50
    },
    {
      id: 'mixture.ref',
      accessorFn: row => row?.mixture?.ref,
      header: () => <span>Référence</span>,
      cell: info => <span>{info.getValue()}</span>,
      footer: props => props.column.id,
      sortingFn: sortingUtils.sortText,
      filterFn: filterFns.includesString,
      enableHiding: true,
      size: 50
    },
    {
      id: 'mixture.reagents',
      accessorFn: row => (row.mixture.description !== "" || row.mixture.description !== null) ? [row.mixture.description] : row.mixture.reagents,
      header: () => <span>Composition</span>,
      cell: (info) => <ReagentsModal mixtureName={info?.row?.original?.mixture?.name} reagents={info?.row?.original?.mixture?.reagents} description={info?.row?.original?.mixture?.description} />,
      footer: props => props.column.id,
      filterFn: filterFns.arrIncludes,
      enableHiding: true,
    },
    {
      id: 'actions',
      accessorFn: row => row?.id,
      header: () => <span></span>,
      cell: info => <ActionCell
        mixtureId={info?.row?.original?.mixture?.id}
        rowIndex={info?.row?.index}
        deleteRow={info?.table?.options?.meta?.removeRow}
        reagents={info?.row?.original?.mixture?.reagents}
        description={info?.row?.original?.mixture?.description}
        mixtureName={info?.row?.original?.mixture?.name}
        canDelete={isInitialConfiguration}
      />,
      footer: props => props.column.id,
      enableResizing: false, //disable resizing for just this column
      enableGlobalFilter: false, //disable global filtering for just this column
      size: 50, //starting column size
    }
  ]

  const [columnsVisibility, setColumnsVisibility] = useState(getColumsVisibility(configurationDef))


  // Mémoiser les fonctions de gestion d'événements
  const handleNewCapsule = useCallback(() => {
    if (!capsulesDropdownOptions || capsulesDropdownOptions.length === 0) return;

    const index = capsulesDropdownOptions.length === 1 ? capsules.length : (capsules.length + capsulesDropdownOptions.length - 1);

    // Get the current selected capsule name (if any)
    const currentCapsuleName = capsulesDropdownOptions[capsulesDropdownOptions.length - 1].name;

    // Use the updated function with the current name
    const name = createNameOptionsFromIndex(index, currentCapsuleName);

    const newCapsule = { id: capsulesDropdownOptions.length, name, databaseId: null };
    setCapsulesDropdownOptions(prev => [...prev, newCapsule]);
  }, [capsulesDropdownOptions, capsules]);

  const handleShowList = useCallback(() => {
    // Update showList state
    setShowList(!showList);
    setIsModified(false);

    const newVisibility = { ...columnsVisibility }
    Object.keys(newVisibility).forEach(key => {
      if (!showList) {
        // When switching to showList=true, only show mixture.name and mixture.subCategory
        newVisibility[key] = (key === 'mixture.name');
      } else {
        // When switching to showList=false, show all columns except capsule.name conditionally
        if (key === 'capsule.name') {
          console.log(key, capsulesDropdownOptions.length)
          newVisibility[key] = capsulesDropdownOptions.length > 1;
        } else {
          newVisibility[key] = true;
        }
      }
    });

    setColumnsVisibility({ ...newVisibility });
  }, [showList, columnsVisibility, capsulesDropdownOptions, isModified]);

  // Add new useEffect to initialize columnsVisibility when component mounts
  useEffect(() => {
    // Initialize the column visibility based on the initial showList value
    const initialVisibility = {};

    Object.keys(columnsVisibility).forEach(key => {
      if (showList) {
        initialVisibility[key] = (key === 'mixture.name');
      } else {
        if (key === 'capsule.name') {
          initialVisibility[key] = capsulesDropdownOptions.length > 1;
        } else {
          initialVisibility[key] = true;
        }
      }
    });

    // Force update by setting with completely new object
    setColumnsVisibility({ ...initialVisibility });
    console.log("Initial visibility set:", initialVisibility);
  }, []); // Empty dependency array ensures this runs once on mount

  // Autres fonctions mémoïsées
  const handleDuplicateEffluences = useCallback((e) => {
    e.stopPropagation();
    setDuplicateEffluences(prev => !prev);
  }, []);

  const handleChangePreTest = useCallback((pretestValue) => (e) => {
    setCapsuleConfiguration((prev) => ({
      ...prev,
      [e.target.name]: !pretestValue
    }));
  }, [setCapsuleConfiguration]);

  const handleSaveConfiguration = useCallback(async () => {
    if (!isModified) return handleShowList();

    setIsSaving(true);

    try {
      const body = { configurationId: selectedConfigurationId, ...formatCapsuleConfigurationForDatabase(capsuleConfiguration, defaultCapsule.id) };
      const response = await api.put(`/capsules/configurations/update/all`, body);

      if (response.status === 200) {
        const formattedConfiguration = formatDefaultConfigurationToAddCapsule(response.data.configurations[0].configuration, defaultCapsule.name, defaultCapsule.id)
        setConfigurations(formattedConfiguration)
        if (response.data.configurations.length > 1) {
          synchronizeConfigurations(response.data.configurations)
        }
        updateCustomerData((prev) => {
          return {
            ...prev,
            capsules: prev.capsules.map(capsule => {
              if (capsule.id === defaultCapsule.id) {
                return {
                  ...capsule,
                  configurations: isNewCapsule ? [response.data.configurations[0]] : capsule.configurations.map(configuration => {
                    if (configuration.id === selectedConfigurationId) {
                      configuration = response.data.configurations[0]
                    }
                    return configuration
                  })
                }
              }
              return capsule
            })
          }
        })


        // Update initialConfiguration with the current state after successful save
        const normalizeConfig = (config) => JSON.stringify(config, Object.keys(config).sort());
        setInitialConfiguration(normalizeConfig(capsuleConfiguration));

        handleShowList()

        toast.success('Configuration mise à jour !')
      } else {
        toast.error('Erreur lors de l\'enregistrement de la configuration, veuillez réessayer')
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement de la configuration, veuillez réessayer')
    } finally {
      setIsSaving(false);
    }
  }, [isModified, capsuleConfiguration, selectedConfigurationId, defaultCapsule, handleShowList]);

  const handleCreateMultipleCapsule = async () => {

    setIsSaving(true); // Déclencher l'état de chargement

    try {
      const divideConfigs = divideConfigurationByCapsule(capsuleConfiguration, defaultCapsule.id)
      const response = await api.post('/capsules/divide', {
        configurations: divideConfigs,
        userId: user.id,
        customerId: customer.id,
        capsuleId: defaultCapsule.id,
        configurationId: selectedConfigurationId,
        duplicateEffluences: duplicateEffluences
      })

      if (response.status === 201) {
        const newCapsules = response.data.slice(1)
        const updatedCapsules = customer.capsules.map((capsule) => {
          if (capsule.id === defaultCapsule.id) {
            return response.data[0]
          }
          return capsule
        })
        updateCustomerData((prev) => {
          return {
            ...prev,
            capsules: [...updatedCapsules, ...newCapsules]
          }
        })

        // Réinitialiser l'état de modification
        setIsModified(false);

        naviguate(`../`)
        toast.success('Configuration mise à jour !')
      } else {
        toast.error('Erreur lors de l\'enregistrement de la configuration, veuillez réessayer')
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la division des capsules, veuillez réessayer')
    } finally {
      setIsSaving(false); // Désactiver l'état de chargement
    }
  }
  const handleCancelDivision = useCallback(() => {
    setIsDivisionMode(false);
    setColumnsVisibility({ ...columnsVisibility, 'capsule.name': false })
    setCapsulesDropdownOptions(defaultDropdownOptions); // Réinitialise les options du dropdown
    setCountByCapsule(getMixtureCountByCapsule(content)); // Réinitialise le comptage
  }, [defaultDropdownOptions]);

  const handleDivisionMode = useCallback(() => {
    setIsDivisionMode(true)
    setColumnsVisibility({ ...columnsVisibility, 'capsule.name': true })
    handleNewCapsule()
  }, [columnsVisibility, handleNewCapsule]);


  const handleDeleteCapsule = useCallback((name) => {
    setCapsulesDropdownOptions(prev => prev.filter(option => option.name !== name))
  }, [])

  useEffect(() => {
    if (isNewCapsule || isInitialConfiguration) {
      handleShowList()
    }
  }, [])

  // Effect pour initialiser la configuration de référence
  useEffect(() => {
    if (capsuleConfiguration && !initialConfiguration) {
      // Use the normalized format for consistent comparisons later
      const normalizeConfig = (config) => {
        return JSON.stringify(config.content, Object.keys(config.content).sort());
      };
      setInitialConfiguration(normalizeConfig(capsuleConfiguration));
      setIsModified(false); // Explicitly set to false on initialization
    }
  }, [capsuleConfiguration, initialConfiguration]);

  // Effect pour détecter les changements dans la configuration
  useEffect(() => {
    if (initialConfiguration && capsuleConfiguration) {
      // Ensure we're comparing the same structure with sorted keys for consistency
      const normalizeConfig = (config) => {
        return JSON.stringify(config.content, Object.keys(config.content).sort());
      };

      const currentConfig = normalizeConfig(capsuleConfiguration);

      setIsModified(currentConfig !== initialConfiguration);
    }
  }, [capsuleConfiguration, initialConfiguration]);

  useEffect(() => {
    if (selectedConfigurationId && isDivisionMode) {
      handleCancelDivision()
    }
  }, [selectedConfigurationId])

  // Assurer que la liste d'ajout (showList) est désactivée en mode validation
  useEffect(() => {
    if (!isInitialConfiguration && showList) {
      handleShowList();
    }
  }, [isInitialConfiguration])

  if (!content) return <div>Error loading configuration</div>
  return (

    <div className='flex flex-col gap-4 items-center justify-between'>
      <div className='flex flex-row gap-8 w-full items-center'>
        <div onClick={() => naviguate('../')} className='btn btn-sm btn-outline cursor-pointer btn-primary w-fit'>
          <ArrowLeftIcon className='w-6 h-6' />
          <h3 className='m-0 text-base'>Retour client</h3>
        </div>
        <SaveConfigurationButton isSaving={isSaving} isModified={isModified} isInitialConfiguration={isInitialConfiguration} showList={showList} handleSaveConfiguration={handleSaveConfiguration} handleShowList={handleShowList} />

        {
          (!showList && capsuleConfiguration.content.length > 0) && (
            <ActionButton
              onClick={isDivisionMode ? handleCancelDivision : handleDivisionMode}
              className={`${isDivisionMode ? 'btn-error' : 'btn-primary'} ${!isInitialConfiguration ? 'btn-disabled' : ''}`}
              disabled={isSaving || !isInitialConfiguration}
            >
              {isDivisionMode ? 'Annuler' : 'Diviser'}
            </ActionButton>
          )
        }
        {
          !showList && !isDivisionMode && isInitialConfiguration &&
          <p className='text-primary text-lg font-bold m-auto'>SENSIBILITÉS ET DILUTIONS</p>
        }

        {isDivisionMode &&
          <p className='text-primary text-lg font-bold m-auto'>DIVISION</p>
        }
        {showList &&
          <p className='text-primary text-lg font-bold m-auto'>SELECTION</p>
        }
        {
          !isDivisionMode && !showList && !isInitialConfiguration &&
          <p className='text-primary text-lg font-bold m-auto'>VALIDATION</p>
        }
        {isDivisionMode &&
          <div className='flex gap-4 self-center '>
            <div className='flex gap-4 overflow-x-auto overflow-y-hidden max-w-xs lg:max-w-lg'>
              {countByCapsule && capsulesDropdownOptions.map((option, index) => (
                <CapsuleBadge key={index} name={option.name} count={countByCapsule[option.name] || 0} onDelete={handleDeleteCapsule} />
              ))}
            </div>
            <div onClick={handleNewCapsule} className='cursor-pointer text-primary self-center'>
              <Plus className='w-6 h-6' />
            </div>

          </div>
        }
        {Object.keys(countByCapsule).length >= 2 && isDivisionMode &&
          <DivisionDropdown
            isModified={isModified}
            isSaving={isSaving}
            duplicateEffluences={duplicateEffluences}
            duplicateEffluencesInput={duplicateEffluencesInput}
            handleDuplicateEffluences={handleDuplicateEffluences}
            handleCreateMultipleCapsule={handleCreateMultipleCapsule}
          />
        }

      </div>
      <div className='flex gap-x-4 items-center justify-center'>
        <PreTestSelector handleChange={handleChangePreTest} value={capsuleConfiguration.mentalDisturbance || false} property={'mentalDisturbance'} disabled={!isInitialConfiguration} />
        <PreTestSelector handleChange={handleChangePreTest} value={capsuleConfiguration.emotionalDisturbance || false} property={'emotionalDisturbance'} disabled={!isInitialConfiguration} />
        <PreTestSelector handleChange={handleChangePreTest} value={capsuleConfiguration.maintainSymptoms || false} property={'maintainSymptoms'} disabled={!isInitialConfiguration} />
        <PreTestSelector handleChange={handleChangePreTest} value={capsuleConfiguration.organicInversion || false} property={'organicInversion'} disabled={!isInitialConfiguration} />
        <PreTestSelector handleChange={handleChangePreTest} value={capsuleConfiguration.emotionalInversion || false} property={'emotionalInversion'} disabled={!isInitialConfiguration} />
      </div>
      <div className={`relative flex flex-row w-full gap-4 transition-all `}>
        {/* Overlay de chargement qui couvre toute la zone de contenu pendant la sauvegarde */}
        {isSaving && (
          <div className="absolute inset-0 bg-base-100/70 flex items-center justify-center z-10">
            <div className="loading loading-spinner loading-lg text-primary"></div>
          </div>
        )}

        {showList && <div className={`w-full shadow-sm border-container p-4 rounded-lg self-start transition-all duration-500`}>
          <AddMixtureTable width={'100%'} />
        </div>
        }

        <div className={`${showList ? 'w-64 flex-shrink-0' : 'w-full'} h-fit shadow-sm border-container p-4 rounded-lg transition-all duration-500`}>
          <CapsuleConfigurationTable
            width={'100%'}
            isDivisionMode={isDivisionMode}
            isInitialConfiguration={!isInitialConfiguration}
            capsulesDropdownOptions={capsulesDropdownOptions}
            columnsVisibility={columnsVisibility}
            setColumnsVisibility={setColumnsVisibility}
            columnsDefs={configurationDef}
            defaultSorting={'mixture.subCategory'}
          />
        </div>
      </div>

    </div>
  )
}

export default React.memo(CapsuleConfiguration)
